import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from "react-dom";
import LanguageSelector from './LanguageSelector'; // Adjust path as needed
import SuccessPopup from '../../utils/popup/SuccessPopup';
import ErrorPopup from '../../utils/popup/ErrorPopup';
import { StatusBadgeSm } from '../../utils/StatusBadge';
import GetCookie from '../../utils/GetCookie';
import DeleteConfirmationModal from '../../utils/DeleteConfirmationModal';
import ProcessingPopup from '../../utils/popup/ProcessingPopup';
import TrimContent from '../../utils/TrimContent';

const abbrSelectionStore = new Map();
const AUTOGEN_TYPE_LABELS = {
  LIST_OF_FIGURES: "List of Figures",
  LIST_OF_TABLES: "List of Tables",
  ABBREVIATIONS: "Abbreviations",
};
const _normShort = (s) => (s || "").trim().toUpperCase();
const _abbrKey = (a) =>
  `${_normShort(a.short_form)}|${(a.full_form || "").trim().toUpperCase()}`;

const ReportModalForm = ({ isOpen, onClose, templateDetails, updFunc, formData }) => {
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [languages, setLanguages] = useState({});
  const [isLoadingLanguages, setIsLoadingLanguages] = useState(false);
  const [selectedFileType, setSelectedFileType] = useState('DOCX');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSuccessPopupVisible, setIsSuccessPopupVisible] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [isErrorPopupVisible, setIsErrorPopupVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Abbreviation States
  const [showAbbrevModal, setShowAbbrevModal] = useState(false);
  const [abbreviations, setAbbreviations] = useState([]);
  const [abbrevSearch, setAbbrevSearch] = useState('');
  const [selectedAbbrevIds, setSelectedAbbrevIds] = useState([]);
  const [isAddingAbbrev, setIsAddingAbbrev] = useState(false);
  const [newShortForm, setNewShortForm] = useState('');
  const [newFullForm, setNewFullForm] = useState('');
  const [newIsPrimary, setNewIsPrimary] = useState(false);
  const [editingAbbrevId, setEditingAbbrevId] = useState(null);
  const [editShortForm, setEditShortForm] = useState('');
  const [editFullForm, setEditFullForm] = useState('');
  const [isSavingAbbrev, setIsSavingAbbrev] = useState(false);
  const [showDeleteAbbrevConfirm, setShowDeleteAbbrevConfirm] = useState(false);
  const [deleteTargetIds, setDeleteTargetIds] = useState([]);
  // Front Page States
  const [frontPageFile, setFrontPageFile] = useState(null);
  const [prevTemplateId, setPrevTemplateId] = useState(null);
  const [selectedAgentIds, setSelectedAgentIds] = useState(new Set());
  const [agentSearch, setAgentSearch] = useState('');
  const [expandedSections, setExpandedSections] = useState({});
  const [selectedSectionKeys, setSelectedSectionKeys] = useState(new Set());
  const [showDependencyWarning, setShowDependencyWarning] = useState(false);
  const [missingDependencies, setMissingDependencies] = useState([]);
  const [selectedDepIds, setSelectedDepIds] = useState(new Set());
  const [primaryAbbrevIds, setPrimaryAbbrevIds] = useState([]);
  const getSelMap = () => {
    const t = templateDetails?.id;
    if (!abbrSelectionStore.has(t)) abbrSelectionStore.set(t, new Map());
    return abbrSelectionStore.get(t);
  };

  // Fetch languages on modal open
  const API_BASE_URL = '/pceapi/genbi';
  
  const headers = {
    'Content-Type': 'application/json',
    Authorization: GetCookie('access_token')
  };
  useEffect(() => {
    if (isOpen) {
      fetchLanguages();
      fetchAbbreviations();
      if (templateDetails?.id !== prevTemplateId) {
        setSelectedAbbrevIds([]);
        setFrontPageFile(null);
        setSelectedLanguage(null);
        setPrimaryAbbrevIds([]);
        setPrevTemplateId(templateDetails?.id);
      }
      // Init agent selector
      if (formData?.sectionOrder?.length > 0) {
        const initSelectedIds = new Set();
        formData.sectionOrder.forEach(secKey => {
          const sec = formData.sectionsDict[secKey];
          if (!sec) return;
          sec.agentOrder.forEach(agKey => {
            const agent = sec.agentsDict[agKey];
            if (agent?.id) initSelectedIds.add(agent.id);
          });
        });
        setSelectedAgentIds(initSelectedIds);
        setSelectedSectionKeys(new Set(formData.sectionOrder.map(k => formData.sectionsDict[k]?.section_ordering_id).filter(Boolean)));
      } else {
        setSelectedAgentIds(new Set());
        setSelectedSectionKeys(new Set());
      }
      setAgentSearch('');
      setExpandedSections({});
    }
    
  }, [isOpen, templateDetails]);

  
  const fetchAbbreviations = async () => {

    try {
      const response = await fetch(`${API_BASE_URL}/get-abbreviations?template_id=${templateDetails.id}`, {
        method: 'GET', 
        headers: headers
      });
      
      const data = await response.json();

      if (data.status === 'success') {
        
        const rawData = data.data?.abbreviations || data.abbreviations || {};
        
        let fetchedList = [];

        
        if (Array.isArray(rawData)) {
         
          fetchedList = rawData;
        } else if (typeof rawData === 'object' && rawData !== null) {
        
          fetchedList = Object.entries(rawData).map(([key, value]) => ({
            id: parseInt(key),
            ...value
          }));
        }
        fetchedList.sort((a, b) => {
             
             const nameA = a.full_form?.toLowerCase() || "";
             const nameB = b.full_form?.toLowerCase() || "";
             return nameA.localeCompare(nameB);
        });

        setAbbreviations(fetchedList);

        const primaryIds = fetchedList
          .filter((abbr) => abbr.is_primary)
          .map((abbr) => abbr.id);
        setPrimaryAbbrevIds(primaryIds);
        // Restore selection: session cache (module store) > DB is_selected
        const selMap = getSelMap();
        let selectedList;
        if (selMap.size > 0) {
          // Session cache exists — hybrid match (exact key + short_form fallback)
          const freshExact = new Set(fetchedList.map((a) => _abbrKey(a)));
          const fallbackShorts = new Set(
            [...selMap.entries()]
              .filter(([k]) => !freshExact.has(k))
              .map(([, short]) => short)
          );
          selectedList = fetchedList.filter(
            (a) => selMap.has(_abbrKey(a)) || fallbackShorts.has(_normShort(a.short_form))
          );
        } else {
          // No session cache — restore from DB is_selected
          selectedList = fetchedList.filter((a) => a.is_selected);
        }
        // Sync module store with current selection
        const healed = new Map(selectedList.map((a) => [_abbrKey(a), _normShort(a.short_form)]));
        abbrSelectionStore.set(templateDetails?.id, healed);
        setSelectedAbbrevIds(selectedList.map((a) => a.id));
      } else {
       
        console.error("Failed to load abbreviations", data);
        setAbbreviations([]); 
      }
    } catch (error) {
      console.error('Error fetching abbreviations:', error);
      setAbbreviations([]);
    } finally {
      
    }
  };

  const fetchLanguages = async () => {
  setIsLoadingLanguages(true);
  setIsLoading(true);
  try {
    const response = await fetch(`${API_BASE_URL}/langs`, {
      method: 'GET',
      headers: headers
    });
    
    const data = await response.json();
    
    if (data.status === 'success') {
      setLanguages(data.langs || {});
    } else {
      setErrorMessage(data.message || 'Failed to fetch languages');
      setIsErrorPopupVisible(true);
    }
  } catch (error) {
    console.error('Error fetching languages:', error);
    setErrorMessage('Network error occurred while fetching languages');
    setIsErrorPopupVisible(true);
  } finally {
    setIsLoadingLanguages(false);
    setIsLoading(false);
  }
};
  const handleLanguageSelect = (language) => {
    setSelectedLanguage(language);
  };

  const handleFileTypeSelect = (fileType) => {
    setSelectedFileType(fileType);
  };

  // Inside ReportModalForm.jsx

const handleGenerate = async (extraAgentIds = null) => {
  if (!selectedFileType || !templateDetails) return;

  setIsGenerating(true);
  await updFunc();

  try {
    // CHANGE: Use FormData to send File + Data together (Matching Lead's Screenshot)
    const formData = new FormData();
    formData.append('template_id', templateDetails.id);
    if (selectedLanguage?.id) {
      formData.append('lang_id', selectedLanguage.id);
    }
    formData.append('type_ext', selectedFileType.toUpperCase());
    const finalAgentIds = extraAgentIds
      ? [...new Set([...selectedAgentIds, ...extraAgentIds])]
      : [...selectedAgentIds];
    formData.append('agent_ids', JSON.stringify(finalAgentIds));
    
    // Send selected abbreviations as a JSON string
    // const selectedAbbrList = abbreviations?.filter(a => selectedAbbrevIds.includes(a.id)) || [];
    const selectedAbbrList =
  abbreviations
    ?.filter(a => selectedAbbrevIds.includes(a.id))
    .map(({ short_form, ...rest }) => ({
      ...rest,
      abbreviation: short_form
    })) || [];

formData.append('abbreviations', JSON.stringify(selectedAbbrList));

    // Send the file if it exists in state
    if (frontPageFile) {
      formData.append('front_page', frontPageFile);
    }

    // CHANGE: Remove 'Content-Type': 'application/json' for FormData
    const multipartHeaders = { ...headers };
    delete multipartHeaders['Content-Type']; 

    const response = await fetch(`${API_BASE_URL}/generate-report`, {
      method: 'POST',
      headers: multipartHeaders, 
      body: formData
    });
      const data = await response.json();
      
      if (data.status === 'success') {
        setSuccessMessage(data.message || 'Report generation started successfully');
        setIsSuccessPopupVisible(true);
      } else {
        setErrorMessage(data.message || 'Failed to generate report');
        setIsErrorPopupVisible(true);
      }
    } catch (error) {
  console.error('Error generating report:', error);
  setErrorMessage('Network error occurred while generating report');
  setIsErrorPopupVisible(true);
} finally {
      setIsGenerating(false);
    }
  };
const handleClose = (cdt = 'cancel') => {
    // Reset state
    setSelectedLanguage(null);
    setSelectedFileType('DOCX');
    setIsGenerating(false);
    onClose(cdt);
  };
  const closeSuccessPopup = () => {
    setIsSuccessPopupVisible(false);
    handleClose('gen')
  };

  
const closeErrorPopup = () => {
  setIsErrorPopupVisible(false);
  handleClose('error');
};
  if (!isOpen) return null;
    const closeAbbrevErrorPopup = () => {
    setIsErrorPopupVisible(false);
  };

  /* --- NEW CODE TO ADD --- */
  const handleFrontPageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // 1. Check for 3MB Limit (3 * 1024 * 1024 bytes)
      if (file.size > 3 * 1024 * 1024) {
        setErrorMessage('File size exceeds 3MB limit');
        setIsErrorPopupVisible(true);
        e.target.value = null; // Reset the input
        return;
      }

      setFrontPageFile(file);
      
    }
  };

  // ── Agent selector helpers ──────────────────────────────────────────────────
  const getSelectableAgentIdsInSection = (sectionKey) => {
    const sec = formData?.sectionsDict?.[sectionKey];
    if (!sec) return [];
    const ids = [];
    sec.agentOrder.forEach(agKey => {
      const agent = sec.agentsDict[agKey];
      if (agent?.id) ids.push(agent.id);
    });
    (formData?.sectionOrder || []).forEach(childKey => {
      if (formData.sectionsDict[childKey]?.parent_section_id === sectionKey) {
        ids.push(...getSelectableAgentIdsInSection(childKey));
      }
    });
    return ids;
  };

  const toggleAgentId = (agentId) => {
    setSelectedAgentIds(prev => {
      const next = new Set(prev);
      if (next.has(agentId)) next.delete(agentId);
      else next.add(agentId);
      return next;
    });
  };

  const toggleSectionAgents = (sectionKey, section) => {
    const allIds = getSelectableAgentIdsInSection(sectionKey);
    const orderingId = section.section_ordering_id;
    if (allIds.length === 0) {
      setSelectedSectionKeys(prev => {
        const next = new Set(prev);
        if (next.has(orderingId)) next.delete(orderingId);
        else next.add(orderingId);
        return next;
      });
      return;
    }
    const allSelected = allIds.every(id => selectedAgentIds.has(id));
    setSelectedAgentIds(prev => {
      const next = new Set(prev);
      allIds.forEach(id => {
        if (allSelected) next.delete(id);
        else next.add(id);
      });
      return next;
    });
  };

  const selectAllAgents = () => {
    const allIds = new Set();
    const allOrderingIds = new Set();
    formData?.sectionOrder?.forEach(secKey => {
      const sec = formData.sectionsDict[secKey];
      if (!sec) return;
      allOrderingIds.add(sec.section_ordering_id);
      sec.agentOrder.forEach(agKey => {
        const agent = sec.agentsDict[agKey];
        if (agent?.id) allIds.add(agent.id);
      });
    });
    setSelectedAgentIds(allIds);
    setSelectedSectionKeys(allOrderingIds);
  };

  const deselectAllAgents = () => {
    setSelectedAgentIds(new Set());
    setSelectedSectionKeys(new Set());
  };

  const toggleSectionExpand = (key) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const getChildSections = (parentKey) => {
    return (formData?.sectionOrder || []).filter(
      key => formData.sectionsDict[key]?.parent_section_id === parentKey
    );
  };

  const isSectionVisible = (sectionKey) => {
    const sec = formData?.sectionsDict?.[sectionKey];
    if (!sec) return false;
    if (!agentSearch.trim()) return true;
    const q = agentSearch.toLowerCase();
    if ((sec.section_name || '').toLowerCase().includes(q)) return true;
    if (sec.agentOrder.some(agKey => (sec.agentsDict[agKey]?.agent_name || '').toLowerCase().includes(q))) return true;
    return getChildSections(sectionKey).some(childKey => isSectionVisible(childKey));
  };

  const renderSectionRow = (sectionKey, depth = 0) => {
    if (!isSectionVisible(sectionKey)) return null;
    const sec = formData?.sectionsDict?.[sectionKey];
    if (!sec) return null;
    const allAgentIds = getSelectableAgentIdsInSection(sectionKey);
    const orderingId = sec.section_ordering_id;
    const isChecked = allAgentIds.length > 0
      ? allAgentIds.every(id => selectedAgentIds.has(id))
      : selectedSectionKeys.has(orderingId);
    const isIndeterminate = allAgentIds.length > 0 && !isChecked && allAgentIds.some(id => selectedAgentIds.has(id));
    const isExpanded = !!expandedSections[sectionKey];
    const childSections = getChildSections(sectionKey);
    const hasContent = sec.agentOrder.length > 0 || childSections.length > 0;
    const q = agentSearch.toLowerCase().trim();
    const visibleAgents = sec.agentOrder.filter(agKey => {
      const agent = sec.agentsDict[agKey];
      if (!agent) return false;
      if (!q) return true;
      return (agent.agent_name || '').toLowerCase().includes(q);
    });
    return (
      <div key={sectionKey} className="filter-picker-section-block">
        <div className="filter-picker-section-header d-flex align-items-center gap-2 ev-card-7 ev-radius-xsmall p-2 mb-1"
          style={{ '--filter-picker-depth': depth }}
          onClick={() => hasContent && toggleSectionExpand(sectionKey)}
        >
          <button type="button" className="btn p-0 border-0 bg-transparent ev-xsmall-p-blue"
            onClick={e => { e.stopPropagation(); hasContent && toggleSectionExpand(sectionKey); }}
            disabled={!hasContent}
          >
            <i className={`bi ${isExpanded ? 'bi-chevron-down' : 'bi-chevron-right'}`}></i>
          </button>
          {/* Section checkbox — toggles all agents in the section */}
          <span
            className={`report-section-indicator ${isChecked || isIndeterminate ? 'indicator-active' : ''} cursor-pointer`}
            title={isChecked ? "Deselect all agents in this section" : "Select all agents in this section"}
            onClick={e => { e.stopPropagation(); toggleSectionAgents(sectionKey, sec); }}
          >
            {isChecked ? (
              <i className="bi bi-check"></i>
            ) : isIndeterminate ? (
              <i className="bi bi-dash"></i>
            ) : (
              <i className="bi bi-dash"></i>
            )}
          </span>
          <span className="ev-xsmall-p-blue ev-bold-reg me-1">S.{orderingId}</span>
          <span className="ev-xsmall-p-blue flex-grow-1 text-truncate min-w-0">{sec.section_name || '(Unnamed)'}</span>
          <span className="ev-xsmall-p-dark">{allAgentIds.filter(id => selectedAgentIds.has(id)).length}/{allAgentIds.length}</span>
        </div>
        {isExpanded && visibleAgents.map(agKey => {
          const agent = sec.agentsDict[agKey];
          const agId = agent?.id;
          const isSel = agId ? selectedAgentIds.has(agId) : false;
          const isDisabled = !agId;
          return (
            <div key={agKey}
              className={`ev-card-8 filter-picker-agent-row d-flex align-items-center p-1 mb-1 ev-radius-xsmall ${!isDisabled ? 'cursor-pointer' : ''}`}
              style={{ '--filter-picker-depth': depth }}
              onClick={() => !isDisabled && toggleAgentId(agId)}
            >
                <input type="checkbox" className="form-check-input m-0 cursor-pointer shadow-none me-1 ms-1"
                  checked={isSel} disabled={isDisabled}
                  onChange={() => !isDisabled && toggleAgentId(agId)}
                  onClick={e => e.stopPropagation()}
                  title={
                    !agId
                      ? "Save template first to select this agent"
                      : ""
                  }
                />
                <span className="ev-xsmall-p-dark me-1 text-nowrap">S.{orderingId}.{agent.agent_ordering_id}</span>
                <span className="ev-small-p-dark flex-grow-1 text-truncate min-w-0">{agent.agent_name || `Agent ${agent.agent_ordering_id}`}</span>
                <span className="ms-2"><StatusBadgeSm status={agent.status || 'PENDING'} /></span>
            </div>
          );
        })}
        {isExpanded && childSections.map(childKey => renderSectionRow(childKey, depth + 1))}
      </div>
    );
  };

  const rootSections = (formData?.sectionOrder || []).filter(
    key => !formData?.sectionsDict?.[key]?.parent_section_id
  );
  const totalAgents = (formData?.sectionOrder || []).reduce((acc, secKey) => {
    const sec = formData?.sectionsDict?.[secKey];
    if (!sec) return acc;
    sec.agentOrder.forEach(agKey => { if (sec.agentsDict[agKey]?.id) acc++; });
    return acc;
  }, 0);
  // ─────────────────────────────────────────────────────────────────────────

  const findMissingDependencies = () => {
    const missing = [];
    const visited = new Set();
    const queue = [...selectedAgentIds];
    while (queue.length > 0) {
      const currId = queue.shift();
      if (visited.has(currId)) continue;
      visited.add(currId);
      let currAgent = null; let currSectionName = '';
      (formData?.sectionOrder || []).forEach(secKey => {
        const sec = formData.sectionsDict[secKey];
        if (!sec) return;
        (sec.agentOrder || []).forEach(agKey => {
          const ag = sec.agentsDict[agKey];
          if (ag?.id === currId) { currAgent = ag; currSectionName = sec.section_name || `S.${sec.section_ordering_id}`; }
        });
      });
      if (!currAgent) continue;
      (currAgent.reference_agent_ids || []).forEach(refId => {
        if (!selectedAgentIds.has(refId) && !missing.find(m => m.agentId === refId)) {
          let refAgent = null; let refSectionName = ''; let refSecAutogen = null;
          (formData?.sectionOrder || []).forEach(secKey => {
            const sec = formData.sectionsDict[secKey];
            if (!sec) return;
            (sec.agentOrder || []).forEach(agKey => {
              const ag = sec.agentsDict[agKey];
              if (ag?.id === refId) {
                refAgent = ag;
                refSectionName = sec.section_name || `S.${sec.section_ordering_id}`;
                refSecAutogen = sec.autogen_after || null;
              }
            });
          });
          if (refAgent) {
            missing.push({ agentId: refId, agentName: refAgent.agent_name || `Agent ${refAgent.agent_ordering_id}`, sectionName: refSectionName, referencedBy: currAgent.agent_name || `Agent ${currAgent.agent_ordering_id}` });
            queue.push(refId);
          }
        }
      });
    }
    return missing;
  };

  const handleGenerateClick = () => {
    const missing = findMissingDependencies();
    if (missing.length > 0) {
      setMissingDependencies(missing);
      setSelectedDepIds(new Set(missing.map(d => d.agentId)));
      setShowDependencyWarning(true);
    } else {
      handleGenerate();
    }
  };

  const addDependencyAndGenerate = () => {
    setShowDependencyWarning(false);
    handleGenerate([...selectedDepIds]);
  };

  const openAbbrevModal = () => {
    setShowAbbrevModal(true);
  };

  const saveAbbreviationSelection = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/save-abbreviation-selection`, {
        method: "POST",
        headers: headers,
        body: JSON.stringify({
          template_id: templateDetails.id,
          selected_ids: selectedAbbrevIds
        })
      });
      const data = await response.json();
      if (data.status !== "success") {
        console.error("Failed to save abbreviation selection", data);
      }
    } catch (error) {
      console.error("Error saving abbreviation selection:", error);
    }
  };

  const toggleAbbrevSelection = (id) => {
    const abbr = abbreviations.find((a) => a.id === id);
    if (abbr) {
      const selMap = getSelMap();
      const key = _abbrKey(abbr);
      if (selMap.has(key)) selMap.delete(key);
      else selMap.set(key, _normShort(abbr.short_form));
    }
    setSelectedAbbrevIds(prev =>
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  const togglePrimaryStatus = (id) => {
    setPrimaryAbbrevIds(prev => {
      if (prev.includes(id)) {
        // Turning OFF - just remove this ID
        return prev.filter(item => item !== id);
      } else {
        // Turning ON - keep it, backend will handle same short_form conflict
        return [...prev, id];
      }
    });
  };
    const cancelAddAbbreviation = () => {
    setIsAddingAbbrev(false);
    setNewShortForm('');
    setNewFullForm('');
    setNewIsPrimary(false);
  };

  const handleAddAbbreviation = async () => {
    if (!newShortForm.trim() || !newFullForm.trim()) return;
    
    // Check for duplicate abbreviation (case-insensitive)
    const duplicate = abbreviations.find(
      abbr => abbr.short_form.toLowerCase() === newShortForm.trim().toLowerCase()
    );
    
    if (duplicate) {
      setErrorMessage('This abbreviation already exists');
      setIsErrorPopupVisible(true);
      return;
    }
    
    setIsSavingAbbrev(true);
    try {
      const response = await fetch(`${API_BASE_URL}/add-abbreviations`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({
          template_id: templateDetails.id,
          abbreviations: [{ 
            short_form: newShortForm.trim(), 
            full_form: newFullForm.trim(),
            is_primary: newIsPrimary
          }]
        })
      });
      const data = await response.json();
      if (data.status === 'success') {
        const skipped = data.data?.skipped || [];
        const inserted = data.data?.inserted || [];

        if (skipped.length > 0 && inserted.length === 0) {
          setErrorMessage(`Full form '${skipped[0].full_form}' already exists for this template`);
          setIsErrorPopupVisible(true);
        } else {
          cancelAddAbbreviation();
        }
        await fetchAbbreviations();
      } else {
        setErrorMessage(data.message || 'Failed to add abbreviation');
        setIsErrorPopupVisible(true);
      }
    } catch (error) {
      console.error('Error adding abbreviation:', error);
      setErrorMessage('Network error while adding abbreviation');
      setIsErrorPopupVisible(true);
    } finally {
      setIsSavingAbbrev(false);
    }
  };

  const startEditAbbreviation = (abbr) => {
    setEditingAbbrevId(abbr.id);
    setEditShortForm(abbr.short_form);
    setEditFullForm(abbr.full_form);
  };

  const cancelEditAbbreviation = () => {
    setEditingAbbrevId(null);
    setEditShortForm('');
    setEditFullForm('');
  };

  const handleUpdateAbbreviation = async () => {
    if (!editShortForm.trim() || !editFullForm.trim()) return;
    setIsSavingAbbrev(true);
    try {
      const response = await fetch(`${API_BASE_URL}/update-abbreviation`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({
          template_id: templateDetails.id,
          abbreviation_id: editingAbbrevId,
          short_form: editShortForm.trim(),
          full_form: editFullForm.trim(),
          is_primary: primaryAbbrevIds.includes(editingAbbrevId)
        })
      });
      const data = await response.json();
      if (data.status === 'success') {
        cancelEditAbbreviation();
        await fetchAbbreviations();
      } else {
        setErrorMessage(data.message || 'Failed to update abbreviation');
        setIsErrorPopupVisible(true);
      }
    } catch (error) {
      console.error('Error updating abbreviation:', error);
      setErrorMessage('Network error while updating abbreviation');
      setIsErrorPopupVisible(true);
    } finally {
      setIsSavingAbbrev(false);
    }
  };

  const promptDeleteAbbreviations = (ids) => {
    setDeleteTargetIds(ids);
    setShowDeleteAbbrevConfirm(true);
  };

  const executeDeleteAbbreviations = async () => {
    setShowDeleteAbbrevConfirm(false);
    setIsSavingAbbrev(true);
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/delete-abbreviations`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({
          template_id: templateDetails.id,
          abbreviation_ids: deleteTargetIds
        })
      });
      const data = await response.json();
      if (data.status === 'success') {
        const selMap = getSelMap();
        abbreviations
          .filter((a) => deleteTargetIds.includes(a.id))
          .forEach((a) => selMap.delete(_abbrKey(a)));
        setSelectedAbbrevIds(prev => prev.filter(id => !deleteTargetIds.includes(id)));
        setDeleteTargetIds([]);
        await fetchAbbreviations();
      } else {
        setErrorMessage(data.message || 'Failed to delete abbreviation(s)');
        setIsErrorPopupVisible(true);
      }
    } catch (error) {
      console.error('Error deleting abbreviation(s):', error);
      setErrorMessage('Network error while deleting abbreviation(s)');
      setIsErrorPopupVisible(true);
    } finally {
      setIsSavingAbbrev(false);
      setIsLoading(false);
    }
  };
  return createPortal(
    <>
      <div className="modal fade show d-block bg-dark bg-opacity-50" style={{ zIndex: "var(--num-1000000000)" }}>
        <div className="modal-dialog modal-xl modal-dialog-centered w-40">
          <div className="modal-content ev-message-card2 d-block ev-card-2 ev-padding-large">
            
            {/* --- UPDATED HEADER WITH ABBREVIATION BUTTON --- */}
            <div className="mb-4 d-flex justify-content-between align-items-center">
              <div>
                <span className="ev-med-p-blue mt-1">Generate Report</span>
                {templateDetails && (
                  <div className="ev-message-card3 ms-2 avatar-tooltip" data-tooltip={templateDetails.name}>
                    <i className="bi bi-kanban me-1"></i> Template: <span className="ev-bold-med"><TrimContent text={templateDetails.name} maxLength={25} /></span>
                  </div>
                )}
              </div>
              <button 
                type="button" 
                className="ev-btn-blue-outline-round"
                onClick={openAbbrevModal}
              >
                Select Abbreviations
              </button>
            </div>

            {/* Modal Body */}
            <div>

            {/* File Type Selection — pill toggle same as Meta Tags */}
            <div className="mb-2">
              <div className="ev-view-toggle-shell w-100">
                <button
                  type="button"
                  className={`ev-view-toggle-btn flex-fill py-2 ${selectedFileType === 'DOCX' ? 'active' : ''}`}
                  onClick={() => handleFileTypeSelect('DOCX')}
                >
                  <i className="bi bi-file-earmark-word-fill me-1 text-primary"></i>
                  Word Report
                </button>
                <button
                  type="button"
                  className={`ev-view-toggle-btn flex-fill py-2 ${selectedFileType === 'PDF' ? 'active' : ''}`}
                  onClick={() => handleFileTypeSelect('PDF')}
                >
                  <i className="bi bi-file-earmark-pdf-fill me-1 text-danger"></i>
                  PDF Report
                </button>
              </div>
            </div>

            {/* --- AGENT SELECTION SECTION --- */}
            {formData?.sectionOrder?.length > 0 && (
              <div className="mb-4">
                <div className="ev-card-8 p-3 report-section-card pb-0">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <div className="d-flex align-items-center gap-2">
                      <span className="ev-small-p-blue ev-bold-reg">
                        <i className="bi bi-cpu me-1"></i>Select Agents for Report
                      </span>
                      <span className="ev-message-card1 ev-btn-xsmall">{selectedAgentIds.size}/{totalAgents}</span>
                    </div>
                    <div className="d-flex gap-1">
                      <button type="button"
                        className="ev-btn-light-fill ev-btn-xsmall ev-bold-reg border avatar-tooltip"
                        data-tooltip={rootSections.every(k => expandedSections[k]) ? 'Collapse all sections' : 'Expand all sections'}
                        onClick={() => {
                          const allExpanded = rootSections.every(k => expandedSections[k]);
                          const next = {};
                          (formData?.sectionOrder || []).forEach(k => { next[k] = !allExpanded; });
                          setExpandedSections(next);
                        }}
                      >
                        <i className={`bi ${rootSections.every(k => expandedSections[k]) ? 'bi-arrows-collapse' : 'bi-arrows-expand'} me-1`}></i>
                        {rootSections.every(k => expandedSections[k]) ? 'Collapse All' : 'Expand All'}
                      </button>
                      <button type="button" className="ev-btn-blue-fill-round ev-btn-xsmall ev-bold-reg" onClick={selectAllAgents}>Select All</button>
                      <button type="button" className="ev-btn-blue-outline-round ev-btn-xsmall ev-bold-reg" onClick={deselectAllAgents}>Deselect All</button>
                    </div>
                  </div>
                  <div className="position-relative mb-2">
                    <input type="text" className="form-control search-input ms-0" placeholder="Search agents..."
                      value={agentSearch} onChange={e => setAgentSearch(e.target.value)}
                    />
                    <button className="ev-btn-lightblue-fill-round search-bar-button">
                      <i className="bi bi-search"></i>
                    </button>
                  </div>
                  {selectedAgentIds.size === 0 && (
                    <div className="ev-alert-card-warning mb-2 ev-btn-small">
                      <i className="bi bi-exclamation-triangle me-1"></i>
                      Select at least one agent to include in the report.
                    </div>
                  )}
                  <div className="report-agent-list-container d-flex flex-column gap-1 scrollable-element">
                    <div className="d-flex flex-column gap-1">
                      {rootSections.map(key => renderSectionRow(key, 0))}
                      {rootSections.every(key => !isSectionVisible(key)) && (
                        <div className="text-center p-3">
                          <i className="bi bi-search ev-fs-25 ev-small-p-blue"></i>
                          <div className="ev-small-p-blue mt-2">No agents match your search</div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Language Selection
            <div className="mb-4">
              <LanguageSelector
                options={languages}
                isLoading={isLoadingLanguages}
                selectedLanguage={selectedLanguage}
                onLanguageSelect={handleLanguageSelect}
                heightClass="vh-10"
              />
              <div className="text-end mt-2">
                <span className="ev-alert-card-warning ev-btn-small ev-radius-xsmall">
                  Leave empty to use template's default language
                </span>
              </div>
            </div> */}

          {/* ---------------------------------- */}
          </div>{/* End Modal Body */}

            {/* Modal Footer */}
            <div className="d-flex justify-content-end align-items-center gap-2 pt-3">
              <button
                type="button"
                className="ev-btn-blue-outline-round ev-bold-reg"
                onClick={()=> handleClose('cancel')}
                disabled={isGenerating}
              >
                Cancel
              </button>
              <button
                type="button"
                className="ev-btn-blue-fill-round ev-bold-reg"
                onClick={handleGenerateClick}
                disabled={!selectedFileType || isGenerating}
              >
                {isGenerating ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2 text-white" role="status" aria-hidden="true"></span>
                    Generating
                  </>
                ) : (
                  'Generate Report'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* --- NEW ABBREVIATION MODAL POPUP --- */}
      {showAbbrevModal && (
        <div className="popup" style={{ zIndex: "var(--num-1000000000)" }}> {/* Higher z-index to overlay on top */}
          <div className="popup-content ev-message-card2 d-block ev-card-2 w-40">
            
            {/* Modal Header */}
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="ev-med-p-blue mb-0">Abbreviations</h5>
              <div className="d-flex align-items-center gap-1">
                <button
                  className="ev-btn-blue-fill-round ev-btn-small ev-bold-reg"
                  onClick={() => { setIsAddingAbbrev(true); cancelEditAbbreviation(); }}
                  disabled={isAddingAbbrev || isSavingAbbrev}
                >
                  <i className="bi bi-plus-circle me-1"></i> Add
                </button>
                {selectedAbbrevIds.length > 0 && (
                  <button
                    className="ev-btn-red-fill-round ev-btn-small ev-bold-reg"
                    onClick={() => promptDeleteAbbreviations(selectedAbbrevIds)}
                    disabled={isSavingAbbrev}
                  >
                    <i className="bi bi-trash me-1"></i> Delete ({selectedAbbrevIds.length})
                  </button>
                )}
               </div>
            </div>
            {/* Select / Deselect All */}
            <div className="d-flex gap-1 mb-2">
              <button className="ev-btn-blue-fill-round ev-btn-xsmall ev-bold-reg" onClick={() => { const m = getSelMap(); m.clear(); abbreviations.forEach(a => m.set(_abbrKey(a), _normShort(a.short_form))); setSelectedAbbrevIds(abbreviations.map(a => a.id)); }}>Select All</button>
              <button className="ev-btn-blue-outline-round ev-btn-xsmall ev-bold-reg" onClick={() => { getSelMap().clear(); setSelectedAbbrevIds([]); }}>Deselect All</button>
            </div>
            {/* Search Bar */}
            <input 
              type="text" 
              className="search-input mb-3" 
              placeholder="Search Abbreviations..." 
              value={abbrevSearch}
              onChange={(e) => setAbbrevSearch(e.target.value)}
            />
            {/* Inline Add Form */}
            {isAddingAbbrev && (
              <div className="ev-card-8 ev-radius-small p-2 mb-3">
                <div className="ev-small-p-blue ev-bold-med mb-2">
                  <i className="bi bi-plus-circle me-1"></i>New Abbreviation
                </div>
                <div className="d-flex gap-2 align-items-center">
                  <input
                    type="text"
                    className="formField2 ev-btn-small ev-fs-12"
                    placeholder="Short Form (e.g. RCT)"
                    value={newShortForm}
                    onChange={(e) => setNewShortForm(e.target.value)}
                    style={{ flex: '1' }}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleAddAbbreviation(); }}
                  />
                  <input
                    type="text"
                    className="formField2 ev-btn-small ev-fs-12"
                    placeholder="Full Form (e.g. Randomized Controlled Trial)"
                    value={newFullForm}
                    onChange={(e) => setNewFullForm(e.target.value)}
                    style={{ flex: '2' }}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleAddAbbreviation(); }}
                  />
                  <div className="text-center d-flex justify-content-center w-15">
                    <div className="form-check form-switch">
                      <input 
                        className="form-check-input" 
                        type="checkbox" 
                        role="switch"
                        checked={newIsPrimary} 
                        onChange={(e) => setNewIsPrimary(e.target.checked)}
                        id="primary-add-new"
                      />
                    </div>
                  </div>
                  <button
                    className="ev-btn-blue-fill-round ev-radius-xsmall ev-icon-btn-padding-small ev-fs-10"
                    onClick={handleAddAbbreviation}
                    disabled={!newShortForm.trim() || !newFullForm.trim() || isSavingAbbrev}
                  >
                    {isSavingAbbrev ? <span className="spinner-border spinner-border-sm text-white" role="status"></span> : <i className="bi bi-check-lg"></i>}
                  </button>
                  <button
                    className="ev-btn-blue-outline-round ev-radius-xsmall ev-icon-btn-padding-small ev-fs-10"
                    onClick={cancelAddAbbreviation}
                  >
                    <i className="bi bi-x-lg"></i>
                  </button>
                </div>
              </div>
            )}
            {/* List Header */}
            <div className="d-flex justify-content-between px-2 mb-2">
              <span
                className="ev-small-p-blue ev-bold-reg"
                style={{ flex: "2", paddingLeft: "28px" }}
              >
                Full Form
              </span>
              <span
                className="ev-small-p-blue ev-bold-reg text-center"
                style={{ flex: "1" }}
              >
                Abbreviation
              </span>
              <span
                className="ev-small-p-blue ev-bold-reg text-center"
                style={{ width: "90px" }}
              >
                Is Primary
              </span>
              <span
                className="ev-small-p-blue ev-bold-reg text-center"
                style={{ width: "70px" }}
              >
                Actions
              </span>
            </div>

            {/* Scrollable List */}
            <div className="table-fixed-height-scrollable flex-grow-1 overflow-auto">
              <div className="d-flex flex-column gap-2">
                {abbreviations
                  .filter(abbr => 
                    abbr.full_form.toLowerCase().includes(abbrevSearch.toLowerCase()) || 
                    abbr.short_form.toLowerCase().includes(abbrevSearch.toLowerCase())
                  )
                  .map((abbr, index) => (
                    <div
                      key={abbr.id}
                      className={`d-flex justify-content-between align-items-center p-2 ev-radius-small ${selectedAbbrevIds.includes(abbr.id) ? 'ev-card-1' : index % 2 === 0 ? 'ev-card-2' : 'ev-card-3'
                        }`}
                    >

                      {editingAbbrevId === abbr.id ? (
                        <>
                          <div className="d-flex align-items-center gap-2" style={{ flex: '2' }}>
                            <input type="checkbox" className="form-check-input" checked={selectedAbbrevIds.includes(abbr.id)} onChange={() => toggleAbbrevSelection(abbr.id)} />
                            <input type="text" className="formField2 ev-btn-small ev-fs-12" value={editFullForm} onChange={(e) => setEditFullForm(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') handleUpdateAbbreviation(); if (e.key === 'Escape') cancelEditAbbreviation(); }} />
                          </div>
                          <div className="text-center" style={{ flex: '1' }}>
                            <input type="text" className="ms-1 formField2 ev-btn-small ev-fs-12 text-center" value={editShortForm} onChange={(e) => setEditShortForm(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') handleUpdateAbbreviation(); if (e.key === 'Escape') cancelEditAbbreviation(); }} />
                          </div>
                          <div className="text-center d-flex justify-content-center w-15">
                            <div className="form-check form-switch">
                              <input 
                                className="form-check-input" 
                                type="checkbox" 
                                role="switch"
                                checked={primaryAbbrevIds.includes(abbr.id)} 
                                onChange={() => togglePrimaryStatus(abbr.id)}
                                id={`primary-edit-${abbr.id}`}
                              />
                            </div>
                          </div>
                          <div className="d-flex gap-1 justify-content-center" style={{ width: '70px' }}>
                            <button className="ev-btn-blue-fill-round ev-radius-xsmall ev-icon-btn-padding-small ev-fs-10" onClick={handleUpdateAbbreviation} disabled={!editShortForm.trim() || !editFullForm.trim() || isSavingAbbrev}>
                              {isSavingAbbrev ? <span className="spinner-border spinner-border-sm text-white" role="status"></span> : <i className="bi bi-check-lg"></i>}
                            </button>
                            <button className="ev-btn-blue-outline-round ev-radius-xsmall ev-icon-btn-padding-small ev-fs-10" onClick={cancelEditAbbreviation}>
                              <i className="bi bi-x-lg"></i>
                            </button>
                          </div>
                          
                        </>
                      ) : (
                        <>
                          <div className="d-flex align-items-center gap-2" style={{ flex: '2' }}>
                            <input type="checkbox" className="form-check-input shadow-sm" checked={selectedAbbrevIds.includes(abbr.id)} onChange={() => toggleAbbrevSelection(abbr.id)} />
                            <span className="ev-small-p-dark avatar-tooltip" data-tooltip={abbr.full_form || 'None'}><TrimContent text={abbr.full_form} maxLength={47} /></span>
                          </div>
                          <span className="text-center" style={{ flex: '1' }}>
                            <span className="ev-message-card1">{abbr.short_form}</span>
                          </span>
                          <div
                            className="text-center d-flex justify-content-center w-15"
                          >
                            <div className="form-check form-switch">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                role="switch"
                                checked={primaryAbbrevIds.includes(abbr.id)} 
                                disabled 
                                id={`primary-edit-${abbr.id}`}
                              />
                            </div>
                          </div>
                          <div
                            className="d-flex gap-1 justify-content-center"
                            style={{ width: "70px" }}
                          >
                            <button
                              className="ev-btn-blue-outline-round ev-radius-xsmall ev-icon-btn-padding-small ev-fs-10"
                              onClick={() => startEditAbbreviation(abbr)}
                              disabled={isSavingAbbrev}
                            >
                              <i className="bi bi-pencil-fill"></i>
                            </button>
                            <button className="ev-btn-red-fill-round ev-radius-xsmall ev-icon-btn-padding-small ev-fs-10" onClick={() => promptDeleteAbbreviations([abbr.id])} disabled={isSavingAbbrev}>
                              <i className="bi bi-trash"></i>
                            </button>
                          </div>
                        </>
                      )}
                  </div>
                ))}
                {abbreviations.filter(abbr =>
                  abbr.full_form.toLowerCase().includes(abbrevSearch.toLowerCase()) ||
                  abbr.short_form.toLowerCase().includes(abbrevSearch.toLowerCase())
                ).length === 0 && (
                    <div className="text-center p-4">
                      <i className="bi bi-journal-x ev-fs-25 ev-small-p-blue"></i>
                      <div className="ev-small-p-blue mt-2">
                        {abbrevSearch ? 'No matching abbreviations found' : 'No abbreviations yet. Click Add to create one.'}
                      </div>
                    </div>
                  )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="d-flex justify-content-end gap-2 mt-3 pt-2 border-top">
              <button className="ev-btn-blue-outline-round" onClick={() => { setShowAbbrevModal(false); cancelEditAbbreviation(); cancelAddAbbreviation(); }}>Cancel</button>
              <button className="ev-btn-blue-fill-round" onClick={() => { saveAbbreviationSelection(); setShowAbbrevModal(false); cancelEditAbbreviation(); cancelAddAbbreviation(); }}>Save</button>
            </div>
          </div>
        </div>
      )}
      <div style={{ position: 'relative', zIndex: 2100 }}>
        <DeleteConfirmationModal
          show={showDeleteAbbrevConfirm}
          onClose={() => { setShowDeleteAbbrevConfirm(false); setDeleteTargetIds([]); }}
          onConfirm={executeDeleteAbbreviations}
          objectName="abbreviation"
          itemName={`${deleteTargetIds.length} abbreviation${deleteTargetIds.length > 1 ? 's' : ''}`}
          bodyContent={
            <p className="text-danger mb-0">
              This action cannot be undone.
            </p>
          }
        />
      </div>

      
      {/* Success Popup */}
      {isSuccessPopupVisible && (
        <SuccessPopup
          onClose={closeSuccessPopup}
          message={successMessage}
        />
        
      )}
      {/* Error Popup */}
{isErrorPopupVisible && (
  <ErrorPopup
    onClose={showAbbrevModal ? closeAbbrevErrorPopup : closeErrorPopup}
    message={errorMessage}
  />
)}

      {/* Dependency Warning Popup */}
      {showDependencyWarning && (
        <div className="popup">
          <div className="ev-card-4 p-4 report-dep-popup-card">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <div className="d-flex align-items-center gap-2">
                <i className="bi bi-exclamation-triangle-fill text-warning report-dep-warning-icon"></i>
                <span className="ev-reg-p-blue ev-bold-reg">Missing Agent Dependencies</span>
              </div>
              <button type="button" className="ev-btn-light-fill ev-btn-xsmall" onClick={() => setShowDependencyWarning(false)}>
                <i className="bi bi-x"></i>
              </button>
            </div>
            <p className="ev-xsmall-p-dark mb-3">
              The following agents are referenced by your selected agents but are not included in the report.
              Missing dependencies may cause broken cross-references. Select which ones to add.
            </p>
            <div className="d-flex align-items-center justify-content-between mb-2">
              <span className="ev-xsmall-p-blue ev-bold-reg">{selectedDepIds.size}/{missingDependencies.length} selected</span>
              <div className="d-flex gap-1">
                <button type="button" className="ev-btn-blue-fill-round ev-btn-xsmall ev-bold-reg"
                  onClick={() => setSelectedDepIds(new Set(missingDependencies.map(d => d.agentId)))}>
                  Select All
                </button>
                <button type="button" className="ev-btn-blue-outline-round ev-btn-xsmall ev-bold-reg"
                  onClick={() => setSelectedDepIds(new Set())}>
                  Clear
                </button>
              </div>
            </div>
            <div className="report-dep-list mb-3">
              <div className="d-flex flex-column gap-1">
                {missingDependencies.map(dep => {
                  const isSel = selectedDepIds.has(dep.agentId);
                  return (
                    <div key={dep.agentId}
                      className={`d-flex align-items-center gap-2 p-2 ev-radius-xsmall cursor-pointer ${isSel ? 'ev-card-1' : 'ev-card-2'}`}
                      onClick={() => setSelectedDepIds(prev => { const next = new Set(prev); if (next.has(dep.agentId)) next.delete(dep.agentId); else next.add(dep.agentId); return next; })}
                    >
                      <input type="checkbox" className="form-check-input m-0 shadow-none cursor-pointer"
                        checked={isSel}
                        onChange={() => setSelectedDepIds(prev => { const next = new Set(prev); if (next.has(dep.agentId)) next.delete(dep.agentId); else next.add(dep.agentId); return next; })}
                        onClick={e => e.stopPropagation()}
                      />
                      <i className="bi bi-link-45deg ev-xsmall-p-blue report-dep-link-icon"></i>
                      <div className="report-dep-info">
                        <div className="ev-small-p-dark ev-bold-reg text-truncate">{dep.agentName}</div>
                        <div className="ev-xsmall-p-dark">{dep.sectionName} · referenced by <em>{dep.referencedBy}</em></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="d-flex justify-content-between align-items-center">
              <button type="button" className="ev-btn-blue-outline-round ev-bold-reg"
                onClick={() => { setShowDependencyWarning(false); handleGenerate(); }}>
                Skip & Generate Anyway
              </button>
              <div className="d-flex gap-2">
                <button type="button" className="ev-btn-white-fill-round ev-bold-reg report-dep-cancel-btn"
                  onClick={() => setShowDependencyWarning(false)}>
                  Cancel
                </button>
                <button type="button" className="ev-btn-blue-fill-round ev-bold-reg"
                  disabled={selectedDepIds.size === 0}
                  onClick={addDependencyAndGenerate}>
                  <i className="bi bi-plus-circle me-1"></i>
                  Add Selected & Generate
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isLoading && (
        <ProcessingPopup message="Processing, please wait..." />
      )}
    </>,
    document.body
  );
};

export default ReportModalForm;